import React from "react";
import PropTypes from "prop-types";
import Immutable from "immutable";
import ImmutablePropTypes from "react-immutable-proptypes";

import { HighlightStringFromLocations } from "components/Common/HighlightString";

import SvgIcon from "components/Common/SvgIcon";
import Tag from "components/Common/Tag";
import { NO_OP_FUNCTION } from "services/helpers";
import { connect } from "react-redux";

import { Response, ResponseFilterItem } from "resourceModels";
import { getResource } from "selectors/resources";
import { replaceVariableIDJSX } from "services/strings";
import { findAndReorderTopTag, isTopTag } from "services/tags";
import classNames from "classnames";

import "./style.scss";

class ResponseListItem extends React.Component {
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.handleClick = this.handleClick.bind(this);
    this.handleTagClick = this.handleTagClick.bind(this);
    this.renderTitle = this.renderTitle.bind(this);
    this.renderDescription = this.renderDescription.bind(this);

    this.answerFlowContainerRef = React.createRef();
  }

  /**
   * Click handler
   */
  handleClick() {
    const { handleClick, response } = this.props;

    handleClick(response);
  }

  /**
   * Tag click handler
   *
   * @param {String} name
   */
  handleTagClick(name) {
    const { handleTagClick } = this.props;

    if (handleTagClick) {
      handleTagClick(name);
    }
  }

  /**
   * handlePrioritizeToptag
   * @param {Object} responseTags
   */
  handlePrioritizeTopTag(responseTags) {
    const { tags } = this.props;

    if (
      (tags.findIndex(tag => (isTopTag(tag.name))) !== -1) &&
      (responseTags.size > 1) && (tags.size > 0)
    ) {
      const wantedTag = tags.filter(tag => (isTopTag(tag.name)));
      const topTagId = wantedTag.get(0).id;

      if (responseTags.get(0).id === topTagId) {
        return responseTags;
      }

      const topTagIndex = responseTags.indexOf(topTagId);

      if (topTagIndex !== -1) {
        return findAndReorderTopTag(topTagIndex, responseTags);
      }
    }

    return responseTags;
  }

  /**
   * Returns the selected response's title
   */
  renderTitle() {
    const {
      /** @type ResponseFilterItem */
      responseFilterItem,
      response
    } = this.props;

    let titleSearchData;

    if (responseFilterItem) {
      titleSearchData = responseFilterItem.handle;
    }

    const responseHandle = response.handle;

    if (!titleSearchData) {
      return responseHandle;
    }

    return (
      <HighlightStringFromLocations
        string={titleSearchData.get("body")}
        locations={titleSearchData.get("highlights") || Immutable.List()}
        variables={Immutable.Map()}
      />
    );
  }


  /**
   * Returns the selected response's description
   *
   * @returns {{ _html: * }}
   */
  renderDescription() {
    const {
      /** @type ResponseFilterItem */
      responseFilterItem,
      response,
      variables
    } = this.props;

    let descriptionSearchData;
    let messageContentSearchData;

    if (responseFilterItem) {
      descriptionSearchData = responseFilterItem.description;
      messageContentSearchData = responseFilterItem.content;
    }

    const responseDescription = response.descriptiveString;

    if (!descriptionSearchData && (!messageContentSearchData || !messageContentSearchData.size)) {
      return <span>{replaceVariableIDJSX(responseDescription, variables)}</span>;
    }


    if (!descriptionSearchData) {
      return (
        <HighlightStringFromLocations
          string={messageContentSearchData.getIn([0, "body"])}
          locations={messageContentSearchData.getIn([0, "highlights"]) || Immutable.List()}
          variables={variables || Immutable.Map()}
        />
      );
    }

    return (
      <HighlightStringFromLocations
        string={descriptionSearchData.get("body")}
        locations={descriptionSearchData.get("highlights") || Immutable.List()}
        variables={Immutable.Map()}
      />
    );
  }

  /**
   * Render dirty, warning, or cloud alert icon
   * @param {Boolean} isDirty
   * @param {Boolean} isValid
   * @returns {ReactElement}
   */
  static renderDirtyAlertIcon(isDirty, isValid) {
    let icon;

    if (isDirty && !isValid) {
      icon = (
        <SvgIcon
          icon="CloudAlert"
          customClassName="ResponseListItem__icons__dirty-icon"
          fillColor="currentColor"
        />
      );
    } else if (isDirty) {
      icon = (
        <SvgIcon
          icon="Cloud"
          customClassName="ResponseListItem__icons__dirty-icon"
          fillColor="currentColor"
        />
      );
    } else if (!isValid) {
      icon = (
        <SvgIcon
          icon="WarningYellow"
          customClassName="ResponseListItem__icons__warning-icon"
        />
      );
    }

    return icon;
  }

  /**
   * renderTags
   * @param {Object} responseTags
   */
  renderTags(responseTags) {
    const { tags } = this.props;

    const arrangedTags = this.handlePrioritizeTopTag(responseTags);

    return (
      arrangedTags.map((tagID) => {
        const tag = tags && tags.find(t => t.id === tagID);

        if (tag) {
          return (
            <Tag
              key={tag.id}
              tag={tag}
              customClassName="ResponseListItem__tags__tag"
              onClick={this.handleTagClick}
              isSmall
              hasIcon={isTopTag(tag.name)}
            />
          );
        }

        return null;
      })
    );
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      /** @type ResponseRecord|Response */
      response,
      responseLoaded,
      hideTags,
      style
    } = this.props;

    if (!response) {
      return null;
    }

    // use the loaded response data, otherwise, use the shallow data
    const {
      isValid, isDirty, rating
    } = responseLoaded || response;

    const {
      handle
    } = response;
    const responseTags = response.tags;

    return (
      <div
        key={response.id}
        style={style}
      >
        {/* Not going to have a keyboard handler */}
        <div // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus, max-len
          className={
            classNames(
              "ResponseListItem",
              "g-side__list__item"
            )
          }
          onClick={this.handleClick}
          role="button"
        >
          <div className="ResponseListItem__content">
            <div className="ResponseListItem__label">
              <span
                className={
                  classNames(
                    "ResponseListItem__content__label",
                    "g-side__list__item__label",
                    {
                      "g-side__list__item__label--light": !handle
                    }
                  )
                }
              >
                {this.renderTitle()}
              </span>

              <div className="ResponseListItem__iconBar">
                {ResponseListItem.renderDirtyAlertIcon(isDirty, isValid)}
                {rating === 1 && (
                  <SvgIcon
                    icon="ThumbsUp"
                    customClassName="ResponseListItem__icons__icon"
                    fillColor="currentColor"
                  />
                )}
                {rating === -1 && (
                  <SvgIcon
                    icon="ThumbsDown"
                    customClassName="ResponseListItem__icons__icon"
                    fillColor="currentColor"
                  />
                )}
                {response.reserved && (
                  <SvgIcon
                    icon="Lock"
                    customClassName="ResponseListItem__icons__icon"
                    fillColor="currentColor"
                  />
                )}
              </div>
            </div>

            <div
              className={classNames(
                "ResponseListItem__content__description",
                "g-side__list__item__description"
              )}
            >
              {this.renderDescription()}
            </div>
            {!hideTags && (
              <div className="ResponseListItem__tags">
                <div className="ResponseListItem__tags__inner">
                  {Boolean(responseTags) && this.renderTags(responseTags)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

/**
 * @param {Object} state
 * @param {Object} ownProps
 * @returns {Object}
 */
function mapState(state, ownProps) {
  const {
    tags, responsesSideBar, responsesPage, responsesLoaded, variables
  } = state;

  const { sourceResponse } = ownProps;

  return {
    client: getResource(state, "client"),
    tags,
    response: sourceResponse || (
      responsesSideBar.find(response => response.id === ownProps.responseId) ||
      getResource(state, "response", ownProps.responseId)
    ),
    responseLoaded: responsesLoaded.get(ownProps.responseId),
    responsesPage,
    variables
  };
}

ResponseListItem.propTypes = {
  responseFilterItem: PropTypes.instanceOf(ResponseFilterItem),
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
  handleClick: PropTypes.func.isRequired,
  handleTagClick: PropTypes.func,
  hideTags: PropTypes.bool,

  response: PropTypes.oneOfType([
    PropTypes.instanceOf(Response),
    PropTypes.instanceOf(Immutable.Record)
  ]).isRequired,
  responseLoaded: PropTypes.instanceOf(Immutable.Record),
  variables: ImmutablePropTypes.map.isRequired,
  tags: ImmutablePropTypes.list
};

ResponseListItem.defaultProps = {
  responseFilterItem: null,
  style: {},
  responseLoaded: null,
  hideTags: false,
  tags: Immutable.List(),
  handleTagClick: NO_OP_FUNCTION
};

const Connector = connect(mapState)(ResponseListItem);

Connector.propTypes = {
  responseFilterItem: PropTypes.instanceOf(ResponseFilterItem)
};

export default Connector;
